import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  ChevronLeft, 
  ChevronRight, 
  Maximize, 
  Minimize, 
  Play, 
  Pause,
  RotateCcw,
  Home,
} from 'lucide-react'

interface EnhancedNavigationProps {
  currentSlide: number
  totalSlides: number
  onNext: () => void
  onPrev: () => void
  onGoToSlide: (index: number) => void
  isFullscreen: boolean
  onToggleFullscreen: () => void
  onGoHome?: () => void
}

const EnhancedNavigation: React.FC<EnhancedNavigationProps> = ({
  currentSlide,
  totalSlides,
  onNext,
  onPrev,
  onGoToSlide,
  isFullscreen,
  onToggleFullscreen,
  onGoHome
}) => {
  const [isAutoPlay, setIsAutoPlay] = useState(false)

  // Desktop nav remains visible at all times (no auto-hide)

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        onNext()
      }, 5000) // 5 seconds per slide

      return () => clearInterval(interval)
    }
  }, [isAutoPlay, onNext])

  const toggleAutoPlay = () => {
    setIsAutoPlay(!isAutoPlay)
  }

  const goToFirstSlide = () => {
    onGoToSlide(0)
  }

  const goToLastSlide = () => {
    onGoToSlide(totalSlides - 1)
  }

  const handleNext = () => {
    onNext()
  }

  const handlePrev = () => {
    onPrev()
  }

  const handleFullscreen = () => {
    onToggleFullscreen()
  }

  // Mobile Navigation Component
  const MobileNavigation = () => (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-slate-900 via-slate-900/95 to-transparent backdrop-blur-xl"
    >
      {/* Slide Progress Bar */}
      <div className="w-full h-1 bg-slate-700">
        <motion.div
          className="h-full bg-gradient-to-r from-bible-gold to-yellow-400"
          initial={{ width: 0 }}
          animate={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Main Controls */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Left Controls */}
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={goToFirstSlide}
              disabled={currentSlide === 0}
              className="p-2 rounded-full bg-slate-800/50 text-bible-gold disabled:opacity-50 disabled:cursor-not-allowed"
              title="First Slide"
            >
              <RotateCcw size={16} />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePrev}
              disabled={currentSlide === 0}
              className="p-3 rounded-full bg-bible-gold/20 text-bible-gold disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous Slide"
            >
              <ChevronLeft size={20} />
            </motion.button>
          </div>

          {/* Center - Slide Info & Auto-play */}
          <div className="flex flex-col items-center gap-1">
            <div className="text-xs text-gray-400">
              {currentSlide + 1} / {totalSlides}
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleAutoPlay}
              className={`p-2 rounded-full transition-colors ${
                isAutoPlay 
                  ? 'bg-bible-gold text-slate-900' 
                  : 'bg-slate-800/50 text-bible-gold'
              }`}
              title={isAutoPlay ? 'Stop Auto-play' : 'Start Auto-play'}
            >
              {isAutoPlay ? <Pause size={16} /> : <Play size={16} />}
            </motion.button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              disabled={currentSlide === totalSlides - 1}
              className="p-3 rounded-full bg-bible-gold/20 text-bible-gold disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next Slide"
            >
              <ChevronRight size={20} />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={goToLastSlide}
              disabled={currentSlide === totalSlides - 1}
              className="p-2 rounded-full bg-slate-800/50 text-bible-gold disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last Slide"
            >
              <RotateCcw size={16} className="rotate-180" />
            </motion.button>
          </div>
        </div>

        {/* Slide Dots */}
        <div className="flex justify-center gap-1 mt-3">
          {Array.from({ length: totalSlides }, (_, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onGoToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-bible-gold scale-125'
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
              title={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  )

  // Desktop Navigation Component
  const DesktopNavigation = () => (
    <motion.div
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="fixed right-4 top-1/2 -translate-y-1/2 z-50 bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-600/50 p-4"
    >
      {/* Main Controls */}
      <div className="flex flex-col gap-3">
        {/* Home Button */}
        {onGoHome && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGoHome}
            className="p-3 rounded-xl bg-slate-800/50 text-bible-gold hover:bg-bible-gold/20 transition-colors"
            title="Go Home"
          >
            <Home size={20} />
          </motion.button>
        )}

        {/* Previous Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handlePrev}
          disabled={currentSlide === 0}
          className="p-3 rounded-xl bg-bible-gold/20 text-bible-gold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bible-gold/30 transition-colors"
          title="Previous Slide"
        >
          <ChevronLeft size={20} />
        </motion.button>

        {/* Auto-play Toggle */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleAutoPlay}
          className={`p-3 rounded-xl transition-colors ${
            isAutoPlay 
              ? 'bg-bible-gold text-slate-900' 
              : 'bg-slate-800/50 text-bible-gold hover:bg-bible-gold/20'
          }`}
          title={isAutoPlay ? 'Stop Auto-play' : 'Start Auto-play'}
        >
          {isAutoPlay ? <Pause size={20} /> : <Play size={20} />}
        </motion.button>

        {/* Next Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleNext}
          disabled={currentSlide === totalSlides - 1}
          className="p-3 rounded-xl bg-bible-gold/20 text-bible-gold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bible-gold/30 transition-colors"
          title="Next Slide"
        >
          <ChevronRight size={20} />
        </motion.button>

        {/* Fullscreen Toggle */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleFullscreen}
          className="p-3 rounded-xl bg-slate-800/50 text-bible-gold hover:bg-bible-gold/20 transition-colors"
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        >
          {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
        </motion.button>
      </div>

      {/* Slide Progress */}
      <div className="mt-4 w-full h-1 bg-slate-700 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-bible-gold to-yellow-400"
          initial={{ width: 0 }}
          animate={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Slide Counter */}
      <div className="text-xs text-gray-400 text-center mt-2">
        {currentSlide + 1} / {totalSlides}
      </div>
    </motion.div>
  )

  return (
    <>
      {/* Mobile Navigation */}
      <div className="md:hidden">
        <MobileNavigation />
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:block">
        <DesktopNavigation />
      </div>
    </>
  )
}

export default EnhancedNavigation
